import DOMPurify from "dompurify";
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import classnames, { ArgumentArray } from "classnames";
import { twMerge } from "tailwind-merge";
import { nextTick } from "vue";
import { getEnv } from "@/env";

export function notEmpty(value) {
    return value !== undefined && value !== null && value !== "";
}

export function convertSecondsToMinutes(seconds) {
    if (seconds < 0) seconds = 0;

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const pad = (v: number) => String(v).padStart(2, "0");

    return hrs > 0
        ? `${hrs}:${pad(mins)}:${pad(secs)}`
        : `${pad(mins)}:${pad(secs)}`;
}

export function decimalToPercentLabel(decimal) {
    return `${(decimal * 100).toFixed(2)}%`;
}

export function createUrlParams(params) {
    let urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (notEmpty(value)) {
            urlParams.append(key, value as any);
        }
    }
    return urlParams;
}

export function copyToClipboard(text) {
    // check to see if the browser supports the copy command
    if (navigator?.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }
    // fallback to the old way of copying
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    return new Promise<void>((resolve, reject) => {
        document.execCommand("copy") ? resolve() : reject();
        textArea.remove();
    });
}

export function currencyFormat(amount?: number | null) {
    if (amount !== null || typeof amount !== "undefined") {
        return ((amount ?? 0) / 100).toFixed(2);
    }
    return 0;
}

export function formatPrice(price, symbol = "") {
    if (price < 0) {
        return `-${symbol}${(Math.abs(price) / 100).toFixed(2)}`;
    } else {
        return `${symbol}${(price / 100).toFixed(2)}`;
    }
}
export function formatDate(date, timezone) {
    let addChar = date.charAt(date.length - 1) === "Z" ? "" : "Z";
    return format(
        utcToZonedTime(parseISO(date + addChar), timezone),
        "MMMM d, yyyy",
    );
}

export function capitalizeFirstLetter(type) {
    return type.charAt(0).toUpperCase() + type.slice(1);
}

export async function wait(ms: number) {
    await new Promise((resolve) => setTimeout(resolve, ms));
    await nextTick();
}

export function slugify(str: string): string {
    return str
    .toLowerCase()
        .trim()
        .replace(/\//g, "-")
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export const siteUrl = getEnv("VITE_APP_URL");

export const pricingUrl = siteUrl + "/pricing";

export function formatCeCredits(credits: number) {
    // if is a whole number, return remove decimal
    if (credits % 1 === 0) {
        return Math.floor(credits);
    }
    return credits.toString();
}

export function classes(...args: ArgumentArray) {
    return twMerge(classnames(...args));
}

export function carbonToDate(date?: string | null) {
    if (!date) {
        return null;
    }
    //2023-08-03T17:42:15.000000Z
    return parseISO(date);
}

/**
 *
 * @param start
 * @param end
 * @returns the difference in seconds between two dates
 */
export function timeDiff(start?: Date | null, end?: Date | null): number {
    if (!start || !end) {
        return 0;
    }
    const diff = end.getTime() - start.getTime();
    return Math.round(diff / 1000);
}

// Links in stored rich text carry target="_blank" (Tiptap adds rel, Quill
// output does not), so force noopener whenever target survives sanitization.
// Guarded because DOMPurify exposes no hooks in non-DOM environments (vitest).
if (DOMPurify.isSupported) {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
            const rel = (node.getAttribute("rel") ?? "")
                .split(/\s+/)
                .filter(Boolean);
            if (!rel.includes("noopener")) {
                rel.push("noopener");
            }
            node.setAttribute("rel", rel.join(" "));
        }
    });
}

/**
 * Sanitize untrusted HTML before rendering. DOMPurify's default allowlist
 * lacks `target`, which both Tiptap and quill-delta-to-html emit on links;
 * `style` is already allowed by default.
 */
export function sanitize(html?: string | null): string {
    return DOMPurify.sanitize(html ?? "", { ADD_ATTR: ["target"] });
}
